import React from 'react';
import { Leaf, Heart, Scale, Award, Flame, Phone } from 'lucide-react';
import { aboutContent, images, foodTruckInfo } from '../data/mock';

const AProposPage = () => {
  const ValueIcon = ({ icon }) => {
    const icons = { Leaf, Heart, Scale, Award };
    const IconComponent = icons[icon];
    return IconComponent ? <IconComponent size={24} /> : null;
  };

  return (
    <main className="pt-20 min-h-screen bg-[#0E0E0E]">
      {/* Hero */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={images.foodTruck}
            alt="Food Truck"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0E0E0E] via-[#0E0E0E]/95 to-[#0E0E0E]" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#F7C948]/10 border border-[#F7C948]/30 text-[#F7C948] text-sm font-medium mb-6">
              <Flame size={16} />
              Notre Histoire
            </div>
            <h1 className="font-display text-5xl md:text-6xl font-extrabold text-white mb-6">
              À propos de <span className="text-gradient-yellow">Just'Une Dalle</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 leading-relaxed">
              {aboutContent.title}
            </p>
          </div>
        </div>
      </section>

      {/* Story */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-white mb-8">
                Notre passion pour la <span className="text-[#F7C948]">street-food</span>
              </h2>
              <div className="space-y-6">
                {aboutContent.paragraphs.map((paragraph, index) => (
                  <p key={index} className="text-gray-400 leading-relaxed text-lg">
                    {paragraph}
                  </p>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="absolute -inset-4 bg-[#F7C948]/10 rounded-3xl blur-3xl" />
              <img
                src={images.burger1}
                alt="Nos créations"
                className="relative w-full h-[400px] object-cover rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 bg-[#1A1A1A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-white mb-4">
              Nos Valeurs
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Ce qui nous anime au quotidien pour vous offrir la meilleure expérience
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {aboutContent.values.map((value, index) => (
              <div
                key={index}
                className="bg-[#0E0E0E] rounded-2xl p-6 border border-white/10 hover:border-[#F7C948]/30 transition-all duration-300 text-center group"
              >
                <div className="w-16 h-16 bg-[#F7C948]/10 rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:bg-[#F7C948]/20 transition-colors duration-300">
                  <span className="text-[#F7C948]">
                    <ValueIcon icon={value.icon} />
                  </span>
                </div>
                <h3 className="font-display text-xl font-bold text-white mb-2">
                  {value.title}
                </h3>
                <p className="text-gray-400 text-sm">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Food Truck Image */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative rounded-3xl overflow-hidden h-[400px] md:h-[500px]">
            <img
              src={images.hero}
              alt="Just'Une Dalle Food Truck"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0E0E0E] via-[#0E0E0E]/50 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-8 md:p-12">
              <h3 className="font-display text-3xl md:text-4xl font-bold text-white mb-2">
                Just'Une Dalle
              </h3>
              <p className="text-gray-300 text-lg mb-4">
                Votre food truck street-food à Malafretaz
              </p>
              <div className="flex flex-wrap gap-4">
                <a
                  href={`tel:${foodTruckInfo.phone.replace(/\s/g, '')}`}
                  className="btn-primary inline-flex items-center gap-2"
                >
                  <Phone size={18} />
                  Nous appeler
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#1A1A1A]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-white mb-4">
            Venez nous rencontrer !
          </h2>
          <p className="text-gray-400 mb-8">
            Nous serions ravis de vous accueillir et de vous faire découvrir nos spécialités
          </p>
          <a
            href={`tel:${foodTruckInfo.phone.replace(/\s/g, '')}`}
            className="btn-primary inline-flex items-center gap-2"
          >
            <Phone size={20} />
            {foodTruckInfo.phone}
          </a>
        </div>
      </section>
    </main>
  );
};

export default AProposPage;
